import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import type { CapaAction } from '@/api/capa/types';
import { AiSuggestionsBanner } from '@/app/(client)/slcp/-components/AiSuggestionsBanner';
import { AiSuggestionsDialog } from '@/app/(client)/slcp/-components/AiSuggestionsDialog';
import { SlcpCapaActionSheet } from '@/app/(client)/slcp/-components/SlcpCapaActionSheet';
import { SlcpCapaTable } from '@/app/(client)/slcp/-components/SlcpCapaTable';
import { SlcpFindingsCard } from '@/app/(client)/slcp/-components/SlcpFindingsCard';
import { useOverviewCapa } from '@/app/(client)/slcp/-hooks/useOverviewCapa';
import { useSlcpOverviewPage } from '@/app/(client)/slcp/-hooks/useSlcpOverviewPage';
import { usePermissions } from '@/hooks/usePermissions';

export const Route = createFileRoute(
  '/(client)/slcp/(pages)/assessments/(details)/$assessmentId/overview/',
)({
  component: SlcpOverviewPage,
});

function SlcpOverviewPage() {
  const { assessmentId } = Route.useParams();
  const {
    t,
    assessment,
    capaTab,
    setCapaTab,
    suggestedDialogOpen,
    setSuggestedDialogOpen,
    suggestedItems,
    suggestedTotal,
    isSuggestedLoading,
    suggestedRowSelection,
    onSuggestedRowSelectionChange,
    selectedSuggestedCount,
    isBulkSubmitting,
    handleBulkAdd,
    closeSuggestedDialog,
    filters: suggestedFilters,
    onFiltersChange: onSuggestedFiltersChange,
    suggestedPagination,
    setSuggestedPagination,
    impactAreasData,
  } = useSlcpOverviewPage(assessmentId);

  const {
    capaPage,
    setCapaPage,
    capaRows,
    capaTotal,
    capaIsLoading,
    capaPageSize,
  } = useOverviewCapa(assessmentId, capaTab);

  const [selectedAction, setSelectedAction] = useState<CapaAction | null>(null);
  const navigate = useNavigate();
  const canCreateCapa = usePermissions().capa.canCreate;

  if (!assessment) return null;

  return (
    <div className='flex flex-col gap-4'>
      {/* The banner leads to bulk-creating CAPA actions from suggestions, so
          it has nothing to offer a viewer without `capa:create`. */}
      {canCreateCapa && (
        <AiSuggestionsBanner
          totalCount={suggestedTotal}
          onView={() => setSuggestedDialogOpen(true)}
          t={t}
        />
      )}

      <SlcpFindingsCard
        assessmentId={assessmentId}
        assessmentCompanyId={assessment.companyId}
        impactAreas={impactAreasData}
        t={t}
      />

      <SlcpCapaTable
        rows={capaRows}
        total={capaTotal}
        isLoading={capaIsLoading}
        page={capaPage}
        pageSize={capaPageSize}
        onPageChange={setCapaPage}
        activeTab={capaTab}
        onTabChange={setCapaTab}
        onRowClick={row =>
          row.kind === 'plan'
            ? navigate({ to: '/capa/plans/$id', params: { id: row.id } })
            : setSelectedAction(row.action)
        }
        impactAreas={impactAreasData}
        t={t}
      />

      <SlcpCapaActionSheet
        open={!!selectedAction}
        onClose={() => setSelectedAction(null)}
        action={selectedAction}
        impactAreas={impactAreasData}
        t={t}
      />

      <AiSuggestionsDialog
        open={suggestedDialogOpen}
        onClose={closeSuggestedDialog}
        items={suggestedItems}
        total={suggestedTotal}
        isLoading={isSuggestedLoading}
        rowSelection={suggestedRowSelection}
        onRowSelectionChange={onSuggestedRowSelectionChange}
        selectedCount={selectedSuggestedCount}
        isSubmitting={isBulkSubmitting}
        onSubmit={handleBulkAdd}
        filters={suggestedFilters}
        onFiltersChange={onSuggestedFiltersChange}
        pagination={suggestedPagination}
        onPaginationChange={setSuggestedPagination}
        impactAreas={impactAreasData}
        t={t}
      />
    </div>
  );
}
